import styled, {keyframes} from 'styled-components'
import themeGet from '@fuegokit/react/utils/theme-get'
import Dialog from '../Dialog2'

export const StyledDialogOverlay = styled(Dialog.Overlay)`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background-color: ${themeGet('colors.blanket.default')};
`
export const StyledScrollableDialogOverlay = styled(StyledDialogOverlay)`
  // scrollable
  overflow: auto;
  display: flex;
  align-items: flex-start;
  justify-content: center;
`
export const StyledDialogContent = styled(Dialog.Content)`
  // ensures good default position for content
  position: fixed;
  top: 0;
  left: 0;
  // styles
  min-width: 304px;
  min-height: 154px;
  padding: ${themeGet('space.8')};
  border-radius: ${themeGet('radii.4')};
  background-color: ${themeGet('colors.elevation.surface.default.[default]')};
  box-shadow: ${themeGet('shadows.elevation.shadow.overlay')};
  // default positioning
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`
export const StyledDialogContentSheet = styled(StyledDialogContent)`
  left: undefined;
  right: 0;
  min-width: 300;
  min-height: 100vh;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
`
export const StyledScrollableDialogContent = styled(StyledDialogContent)`
  overflow: auto;
  max-height: 304px;
`
export const StyledContentInScrollableOverlay = styled(StyledDialogContent)`
  margin-top: ${themeGet('space.8')};
  margin-bottom: ${themeGet('space.8')};
`
export const StyledChromaticContent = styled(StyledDialogContent)`
  padding: ${themeGet('space.3')};
  min-width: auto;
  min-height: auto;
`
// animations
const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`
const fadeOut = keyframes`
  from {
    opacity: 1
  }
  to {
    opacity: 0
  }
`
const scaleIn = keyframes`
  from {
    transform: translate(-50%, -50%) scale(0.75)
  }
  to {
    transform: translate(-50%, -50%) scale(1)
  }
`
const overlayShow = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`
const contentShow = keyframes`
  from {
    opacity: 0;
    transform: translate(-50%, -48%) scale(0.96);
  }
  to {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }
`

export const StyledAnimatedDialogOverlay = styled(StyledDialogOverlay)`
  &[data-state='open'] {
    animation: ${fadeIn} 300ms ease-out;
  }
  &[data-state='closed'] {
    animation: ${fadeOut} 300ms ease-in;
  }
`
export const StyledAnimatedDialogContent = styled(StyledDialogContent)`
  &[data-state='open'] {
    animation: ${fadeIn} 150ms ease-out, ${scaleIn} 200ms ease-out;
  }
  &[data-state='closed'] {
    animation: ${fadeOut} 300ms ease-in;
  }
`
export const StyledDialogOverlayAttr = styled(StyledDialogOverlay)`
  background-color: ${themeGet('colors.elevation.surface.default.[default]')};
  border: ${themeGet('colors.border.accent.blue')};
  padding: ${themeGet('space.4')};

  &[data-state='closed'] {
    border-color: ${themeGet('colors.border.accent.red')};
  }
  &[data-state='open'] {
    border-color: ${themeGet('colors.border.accent.green')};
  }
`
export const StyledDialogContentAttr = styled(StyledChromaticContent)`
  background-color: ${themeGet('colors.elevation.surface.default.[default]')};
  border: ${themeGet('colors.border.accent.blue')};
  padding: ${themeGet('space.4')};

  &[data-state='closed'] {
    border-color: ${themeGet('colors.border.accent.red')};
  }
  &[data-state='open'] {
    border-color: ${themeGet('colors.border.accent.green')};
  }
`
export const StyledDialogCloseAttr = styled(Dialog.Close)`
  background-color: ${themeGet('colors.elevation.surface.default.[default]')};
  border: ${themeGet('colors.border.accent.blue')};
  padding: ${themeGet('space.4')};

  &[data-state='closed'] {
    border-color: ${themeGet('colors.border.accent.red')};
  }
  &[data-state='open'] {
    border-color: ${themeGet('colors.border.accent.green')};
  }
`
export const StyledDialogTriggerAttr = styled(Dialog.Trigger)`
  background-color: ${themeGet('colors.elevation.surface.default.[default]')};
  border: ${themeGet('colors.border.accent.blue')};
  padding: ${themeGet('space.4')};

  &[data-state='closed'] {
    border-color: ${themeGet('colors.border.accent.red')};
  }
  &[data-state='open'] {
    border-color: ${themeGet('colors.border.accent.green')};
  }
`
// demo styles
export const StyledDialogDemo = styled(Dialog)`
  /* reset */
  button,
  fieldset,
  input {
    all: unset;
  }
`
export const StyledDialogDemoOverlay = styled(Dialog.Overlay)`
  background-color: ${themeGet('colors.blanket.default')};
  position: fixed;
  inset: 0;
  animation: ${overlayShow} 150ms cubic-bezier(0.16, 1, 0.3, 1);
`
export const StyledDialogDemoContent = styled(Dialog.Content)`
  background-color: ${themeGet('colors.elevation.surface.default.[default]')};
  border-radius: 6px;
  box-shadow: hsl(206 22% 7% / 35%) 0px 10px 38px -10px, hsl(206 22% 7% / 20%) 0px 10px 20px -15px;
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 90vw;
  max-width: 450px;
  max-height: 85vh;
  padding: 25px;
  animation: ${contentShow} 150ms cubic-bezier(0.16, 1, 0.3, 1);
  &:focus {
    outline: none;
  }
`
export const StyledDialogDemoTitle = styled(Dialog.Title)`
  margin: 0;
  font-weight: ${themeGet('fontWeights.semibold')};
  color: ${themeGet('colors.text.default')};
  font-size: ${themeGet('fontSizes.4')};
`
export const StyledDialogDemoDescription = styled(Dialog.Description)`
  margin: ${themeGet('space.2')} 0 ${themeGet('space.5')};
  color: ${themeGet('colors.text.subtle')};
  font-size: ${themeGet('fontSizes.3')};
  line-height: ${themeGet('lineHeights.default')};
`
export const StyledDialogDemoButton = styled.button`
  align-items: baseline;
  border-width: 0;
  box-sizing: border-box;
  display: inline-flex; // Margins don't collapse with inline-flex, which helps with preceding/trailing visuals and button content spacing
  font-size: inherit;
  font-style: normal;
  font-weight: 500;
  max-width: 100%;
  position: relative;
  text-align: center;
  text-decoration: none;

  height: 2.28571429em;
  line-height: 2.28571em;
  padding: 0 ${themeGet('space.2')};
  border-radius: ${themeGet('radii.1')};
  font-size: ${themeGet('fontSizes.3')};
  font-weight: ${themeGet('fontWeights.normal')};
  vertical-align: middle;
  justify-content: center;
  width: revert;

  transition: background-color 0.1s ease-out, box-shadow 0.15s cubic-bezier(0.47, 0.03, 0.49, 1.38);
  white-space: nowrap;
  user-select: none;
  background-color: ${themeGet('colors.background.neutral.default.[default]')};
  color: ${themeGet('colors.text.default')};

  &:hover {
    cursor: pointer;
    transition: background-color 0s ease-out, box-shadow 0.15s cubic-bezier(0.47, 0.03, 0.49, 1.38);
    background-color: ${themeGet('colors.background.accent.purple.bolder')};
    background-color: ${themeGet('colors.background.neutral.default.hovered')};
    color: ${themeGet('colors.text.default')};
    text-decoration: inherit;
  }
`
export const StyledDialogDemoIconButton = styled.button`
  all: unset;
  font-family: inherit;
  border-radius: ${themeGet('radii.2')};
  height: ${themeGet('space.6')};
  width: ${themeGet('space.6')};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: ${themeGet('colors.background.neutral.subtle.default')};
  color: ${themeGet('colors.text.default')};
  position: absolute;
  top: ${themeGet('space.3')};
  right: ${themeGet('space.3')};
  &:hover {
    cursor: pointer;
    background-color: ${themeGet('colors.background.neutral.subtle.hovered')};
  }
  &:focus {
    box-shadow: 0 0 0 2px ${themeGet('colors.border.focused')};
  }
`
export const StyledDialogDemoFieldset = styled.fieldset`
  all: unset;
  display: flex;
  gap: 20px;
  align-items: center;
  margin-bottom: 15px;
`
export const StyledDialogDemoLabel = styled.label`
  font-size: ${themeGet('fontSizes.2')};
  color: ${themeGet('colors.text.accent.purple.bolder')};
  width: ${themeGet('space.11')};
  text-align: right;
`
export const StyledDialogDemoInput = styled.input`
  all: unset;
  width: 100%;
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: ${themeGet('radii.1')};
  padding: 8px 6px;
  height: ${((8 * 4.5) / 14).toFixed(2)}em;
  font-size: ${themeGet('fontSizes.3')};
  line-height: ${(8 * 2.5) / 14};
  color: ${themeGet('colors.text.subtle')};
  box-shadow: 0 0 0 1px ${themeGet('colors.background.accent.purple.subtle')};
  height: 35px;
  &:focus {
    box-shadow: 0 0 0 2px ${themeGet('colors.border.focused')};
  }
`
//
export const StyledRadixDialogContent = styled(Dialog.Content)`
  position: fixed;
  background: ${themeGet('colors.elevation.surface.default.[default]')};
  border: 1px solid ${themeGet('colors.border.default')};
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  padding: 30px;
`
